import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, errorText } from '../api';
import { NutzungHinweis } from '../billtoro/NutzungHinweis';
import { fehlendeFunktion, grenzeAnbieten } from '../billtoro/upgrade';
import { ConfirmDialog } from '../components/Dialog';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import { isBilltoro } from '../produkt';
import { angebotRechnungUrl, type AngebotRechnung } from './AngebotDaten';
import { rechnungAusAuftragUrl, rechnungAusLieferscheinenUrl, type AuftragRechnung } from './AuftragDaten';
import { ChipRadios } from './ChipRadios';
import { empfehlung, isHourly } from './empfehlung';
import {
  customerBody,
  deQty,
  dePrice,
  drittlandArt,
  emptyRow,
  free,
  hinweisAus,
  kundeWerteOf,
  LEERER_KUNDE,
  plusTage,
  positionBody,
  rateAus,
  ratesFor,
  rowOf,
  skontoBody,
  skontoWerteOf,
  summen,
  type Art,
  type KundeWerte,
  type PosRow,
  type SkontoWerte,
} from './formular';
import type { KontaktDatei } from './kontakte/KontaktDaten';
import { KundeWahl, XRechnungWahl } from './KundeWahl';
import { useLeistungen } from './LeistungDaten';
import { PdfDialog } from './PdfDialog';
import { Positionen, SteuerHinweisFeld, Summen, useSteuerHinweis } from './Positionen';
import { Stundenzettel, type KiPosition } from './Stundenzettel';
import { StundenzettelFenster } from './StundenzettelFenster';
import { darf, fehlerText, useSchmal } from './tarifHinweis';
import type { Kunde, Overview, Rate, RtArt, Vorlage } from './types';
import { urlId } from './urlId';
import { useWaehrung, WaehrungFeld } from './WaehrungFeld';
import { waehrungAus, type Waehrung } from './waehrung';
import { ZahlungBlock } from './ZahlungBlock';
import './formular.css';

const BILLTORO = isBilltoro();

const ARTEN: [RtArt, string][] = [
  ['normal', 'Rechnung'],
  ['abschlag', 'Abschlagsrechnung'],
  ['schluss', 'Schlussrechnung'],
];
const ART_TEXT: Record<RtArt, string> = {
  normal: '',
  abschlag: 'Anzahlung vor oder während der Arbeit.',
  schluss: 'Am Ende: ganze Leistung, die Abschläge werden abgezogen.',
};

// Formularstand eines Entwurfs (rt_entwuerfe.daten), v: Fassung. Auch für Entwürfe aus Stundenzetteln (StundenzettelStapel.tsx)
export interface EntwurfDaten {
  v: 1;
  art: RtArt;
  auftrag: string;
  picked: string[];
  kunde: KundeWerte;
  xrechnung: boolean;
  // ki: Vorschlag der KI (Entwürfe aus Stundenzetteln), gelb bis zur ersten Änderung; bleibt beim Speichern, solange die
  // Zeile nicht geändert ist
  rows: (Omit<PosRow, 'key' | 'ki' | 'tip' | 'touched'> & { ki?: boolean })[];
  serviceFrom: string;
  serviceTo: string;
  paymentDays: string;
  skonto: SkontoWerte | null;
  note: string;
  site: string;
  // Hinweis oben (z. B. Rechnung neu schreiben)
  hinweis: string | null;
  // aus diesem Angebot (fehlt bei älteren Entwürfen)
  angebotId?: string | null;
  // aus dieser Auftragsbestätigung bzw. diesen Lieferscheinen (fehlt bei älteren Entwürfen)
  auftragId?: string | null;
  lieferscheinIds?: string[];
  // Billtoro: aus diesen erfassten Zeiten (zeiten/AbrechnenDialog.tsx; fehlt sonst)
  zeitIds?: string[];
  // „Neu schreiben“: ersetzt diese stornierte Rechnung (Billtoro: ihre Stundenzettel gehören dann zur neuen; fehlt sonst)
  ersetzt?: string | null;
  // Hinweis bei 0 % USt (fehlt bei älteren Entwürfen)
  steuerHinweis?: string;
  // Rechnungsdatum, wenn nicht heute gewählt (leer oder fehlt: heute beim Weiterschreiben), Rechnungsnummer von Hand
  // (leer oder fehlt: automatisch)
  rechnungsdatum?: string;
  nummer?: string;
  // Währung, Kurs (Euro je Einheit) und Tag des Kurses (leer: Rechnungsdatum); fehlt bei älteren Entwürfen: Euro
  waehrung?: Waehrung;
  kurs?: string | null;
  kursDatum?: string | null;
}

// Fehler beim Schreiben zu den Zeiten (zeitstand.ts zeitenPruefen), nach denen „Zeiten lösen“ hilft (dazu 404 „Zeit nicht
// gefunden.“)
const ZEITEN_LOESEN = ['zeit_berechnet', 'zeit_nicht_berechnen', 'zeiten_ohne_kunde', 'zeiten_kunden'];

// „Ersetzt Rechnung RE-… vom …“ steht im Hinweisfeld der neuen Rechnung, ein alter Ersetzt-Satz fällt weg
const ERSETZT = /^Ersetzt Rechnung \S+ vom \d{2}\.\d{2}\.\d{4}\.\s*/;

// Neue Rechnung: Kunde (aus der Kundenliste oder neu), Positionen, Rechnungsdatum (Vorgabe heute), Rechnungsnummer (leer:
// automatisch, Sam 14.10.), Leistungsdatum, Zahlungsziel und Skonto. Vorschau als PDF, dann schreiben (Nummer wird
// vergeben), oder als Entwurf speichern (ohne Prüfung, ohne Nummer).
// Aufruf mit ?kunde=<id> (Kunde vorgewählt), ?vorlage=<id> (Kopie einer Rechnung), ?vorlage=<id>&neuschreiben=1 (nach
// „Neu schreiben“: Kopie mit Hinweis „Ersetzt Rechnung …“), ?entwurf=<id> (Entwurf weiterschreiben) oder
// ?angebot=<id>[&art=abschlag&prozent=30 | &art=schluss] (Rechnung aus einem Angebot, Angebote.tsx), ?auftrag=<id> (aus
// einer Auftragsbestätigung) oder ?lieferscheine=<id>,<id> (aus Lieferscheinen, Auftraege.tsx). Billtoro: Rechnungen aus
// erfassten Zeiten kommen als Entwurf mit zeitIds (zeiten/AbrechnenDialog.tsx); der Server prüft die Zeiten beim Schreiben.
export function RechnungForm({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const navigate = useNavigate();
  const toast = useToast();
  const ku = data.absender.kleinunternehmer;
  const leistungen = useLeistungen(data.firm.id);
  const [art, setArt] = useState<RtArt>('normal');
  const [auftrag, setAuftrag] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [kunde, setKundeState] = useState<KundeWerte>(LEERER_KUNDE);
  const [xrechnung, setXrechnung] = useState(false);
  const [rows, setRows] = useState<PosRow[]>(() => [emptyRow()]);
  // Kunde außerhalb der EU: Vorschläge für den Hinweis bei 0 %; bei einer Firma neue Positionen mit 0 %
  const drittland = drittlandArt(kunde);
  const steuerHinweis = useSteuerHinweis(rows, ku, data.hinweis0, drittland);
  // Rechnungsdatum (leer: heute) und Rechnungsnummer von Hand (leer: automatisch); Kopie, Neu schreiben, Angebot: heute
  const [rechnungsdatum, setRechnungsdatum] = useState(data.today);
  // Währung und Kurs (vorbelegt mit der Währung des Kunden; Kurs vom Rechnungsdatum, solange nicht geändert)
  const wae = useWaehrung({
    firmId: data.firm.id,
    tarif: data.tarif,
    datum: rechnungsdatum || data.today,
    mitDatum: true,
    preiseDa: rows.some((r) => r.price.trim() !== ''),
  });
  const [nummer, setNummer] = useState('');
  const [serviceFrom, setServiceFrom] = useState(data.today);
  const [serviceTo, setServiceTo] = useState('');
  const [paymentDays, setPaymentDays] = useState(String(data.absender.paymentDays));
  const [skonto, setSkonto] = useState<SkontoWerte | null>(null);
  const [note, setNote] = useState('');
  const [site, setSite] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Meldung zur Kleinunternehmer-Grenze: daneben der Weg zu „Meine Angaben“ (Umsätze dort aufgeklappt)
  const [kuGrenze, setKuGrenze] = useState(false);
  // Billtoro, Rechnung aus Zeiten: Zeiten inzwischen weg, schon berechnet oder „nicht berechnen“ → Knopf „Zeiten lösen“;
  // Zeiten eines anderen Kunden → Meldung am Feld Kunde
  const [zeitenLoesen, setZeitenLoesen] = useState(false);
  const [kundeFehler, setKundeFehler] = useState<string | null>(null);
  const kundeFehlerRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (kundeFehler) kundeFehlerRef.current?.scrollIntoView({ block: 'center' });
  }, [kundeFehler]);
  // Tarif-Fehler (Billtoro) zeigt das Fenster „Mehr mit Billtoro“, hier nichts
  const fehler = (err: unknown) => {
    const code = err instanceof ApiError ? err.code : undefined;
    if (code === 'zeiten_kunde') {
      setError(null);
      setKundeFehler(errorText(err));
      return;
    }
    setError(fehlerText(err));
    setKuGrenze(code === 'ku_grenze');
    setZeitenLoesen(
      err instanceof ApiError &&
        ((err.status === 404 && err.message === 'Zeit nicht gefunden.') || (!!code && ZEITEN_LOESEN.includes(code))),
    );
  };
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [params] = useSearchParams();
  // IDs aus der Adresse nur als UUID (urlId.ts)
  const vorlageId = urlId(params.get('vorlage'));
  const neuSchreiben = !!vorlageId && params.get('neuschreiben') === '1';
  const kundeParam = urlId(params.get('kunde'));
  const entwurfParam = urlId(params.get('entwurf'));
  const [entwurfId, setEntwurfId] = useState<string | null>(entwurfParam);
  const [hinweis, setHinweis] = useState<string | null>(null);
  // Hinweis, mit dem der Entwurf gespeichert war (z. B. „Vorschlag der KI …“): bleibt beim nächsten Speichern
  const entwurfHinweis = useRef<string | null>(null);
  // „Neu schreiben“: die stornierte Rechnung, die die neue ersetzt (Server: ersetzt)
  const [ersetzt, setErsetzt] = useState<string | null>(null);
  // Billtoro: Stundenzettel aus dem Stapel am Entwurf bzw. an der Rechnung, die ersetzt wird (beim Schreiben gehen sie an
  // die neue Rechnung)
  const [zettel, setZettel] = useState<KontaktDatei[]>([]);
  const [zettelOffen, setZettelOffen] = useState(false);
  const angebotParam = urlId(params.get('angebot'));
  // die Rechnung wird mit diesem Angebot verbunden (Server: angebotId)
  const [angebotId, setAngebotId] = useState<string | null>(null);
  // aus Auftragsbestätigung bzw. Lieferscheinen (Server: auftragId, lieferscheinIds; höchstens 20)
  const auftragParam = urlId(params.get('auftrag'));
  const lsParam = (params.get('lieferscheine') ?? '').split(',').map(urlId).filter((x): x is string => !!x).slice(0, 20).join(',');
  const [auftragId, setAuftragId] = useState<string | null>(null);
  const [lieferscheinIds, setLieferscheinIds] = useState<string[]>([]);
  // Billtoro: aus diesen Zeiten (Entwurf aus „Rechnung aus Zeiten“, „Neu schreiben“); höchstens 500
  const [zeitIds, setZeitIds] = useState<string[]>([]);
  const ausVorgang = !!(auftragParam || lsParam);
  const [laden, setLaden] = useState(!!(vorlageId || entwurfParam || angebotParam || ausVorgang));
  // Übersicht zum Zeitpunkt des Ladens (die Effekte unten laufen nur einmal je Adresse)
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  const rates: Rate[] = ratesFor(ku);
  const satz: Rate = drittland === 'firma' && !ku ? 0 : 19;
  // Kunde außerhalb der EU (Firma) gewählt oder nicht mehr: leere Positionen mit dem passenden Satz
  useEffect(() => {
    setRows((cur) => (cur.some((r) => free(r) && !r.tip && r.rate !== satz) ? cur.map((r) => (free(r) && !r.tip ? { ...r, rate: satz } : r)) : cur));
  }, [satz]);

  // Billtoro: Stundenzettel der Rechnung, die ersetzt wird, dazu (ohne Recht oder Zettel: keine)
  function zettelVon(rechnungId: string, live: () => boolean) {
    api
      .get<{ stundenzettel: KontaktDatei[] }>(`/api/rechnungstool/rechnung/${rechnungId}/stundenzettel`)
      .then((r) => live() && setZettel((z) => [...z, ...r.stundenzettel]))
      .catch(() => undefined);
  }

  // Kundenart geändert: Behörden bekommen XRechnung vorgeschlagen
  function setKunde(v: KundeWerte) {
    if (v.art !== kunde.art && v.art === 'behoerde') setXrechnung(true);
    if (v.kundeId !== kunde.kundeId) setKundeFehler(null);
    setKundeState(v);
  }
  // Kunde aus der Liste gewählt: Zahlungsziel, XRechnung und Währung aus dem Kunden; „Anderen Kunden wählen“: wieder die
  // Vorgaben
  function onPick(k: Kunde | null) {
    setPaymentDays(String(k?.paymentDays ?? data.absender.paymentDays));
    setXrechnung(!!k && (!!k.xrechnung || artOfKunde(k) === 'behoerde'));
    wae.setW(waehrungAus(k?.waehrung));
  }

  // /rechnung/neu?kunde=<id> (z. B. „Rechnung schreiben“ beim Kunden): Kunde vorgewählt
  useEffect(() => {
    if (!kundeParam || vorlageId || entwurfParam || angebotParam || ausVorgang) return;
    const k = dataRef.current.kunden.find((x) => x.id === kundeParam);
    if (!k) return;
    const w = kundeWerteOf(k);
    setKundeState(w);
    if (w.art === 'behoerde') setXrechnung(true);
    onPick(k);
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kundeParam]);

  // „Kopieren“ aus der Liste: Kunde, Positionen und Hinweis der alten Rechnung, Datum neu. „Neu schreiben“: dazu
  // Leistungszeitraum und Abschläge der alten Rechnung und „Ersetzt Rechnung …“ im Hinweis
  useEffect(() => {
    if (!vorlageId) return;
    let live = true;
    api
      .get<Vorlage>(`/api/rechnungstool/rechnung/${vorlageId}/vorlage`)
      .then((v) => {
        if (!live) return;
        const d = dataRef.current;
        const old = d.items.find((i) => i.id === vorlageId);
        const kundeId = v.kundeId && d.kunden.some((k) => k.id === v.kundeId) ? v.kundeId : null;
        setKundeState(kundeWerteOf(v.customer, kundeId));
        setXrechnung(v.eFormat === 'xrechnung');
        // Währung wie die alte Rechnung, der Kurs zum neuen Datum neu
        wae.setW(waehrungAus(v.waehrung), false);
        setAuftrag(v.auftrag ?? '');
        setRows(v.items.map((i) => rowOf(i)));
        steuerHinweis.laden(v.items, v.steuerHinweis);
        setSite(v.site ?? '');
        setSkonto(skontoWerteOf(v.skonto));
        if (v.paymentDays != null) setPaymentDays(String(v.paymentDays));
        const alt = (v.note ?? '').replace(ERSETZT, '');
        if (neuSchreiben && old) {
          // die neue Rechnung wieder mit Auftragsbestätigung bzw. Lieferscheinen der alten verbinden, sonst mit ihrem
          // Angebot (Lager: so bucht sie nicht noch einmal, was ein Lieferschein zum Angebot schon gebucht hat)
          setAngebotId(v.angebotId ?? null);
          setAuftragId(v.auftragId ?? null);
          setLieferscheinIds(v.lieferscheinIds ?? []);
          // Billtoro: auch wieder mit den Zeiten der alten (nur wenn der Login sie alle sieht) und ihren Stundenzetteln
          setZeitIds(v.zeitIds ?? []);
          setErsetzt(vorlageId);
          if (BILLTORO) zettelVon(vorlageId, () => live);
          setNote(`Ersetzt Rechnung ${old.number} vom ${fmtDate(old.invoiceDate)}.${alt ? `\n${alt}` : ''}`.slice(0, 500));
          if (v.serviceFrom) setServiceFrom(v.serviceFrom);
          setServiceTo(v.serviceTo ?? '');
          // Schlussrechnung: dieselben Abschläge wieder abziehen (nach dem Storno sind sie frei)
          if (old.art === 'schluss') {
            setArt('schluss');
            setPicked(d.abschlaege.filter((x) => old.abzuege.includes(x.number)).map((x) => x.id));
          } else setArt(v.art);
          setHinweis(
            `Neu schreiben: ${old.number} ist storniert${old.stornoNumber ? ` (Stornorechnung ${old.stornoNumber})` : ''}. Bitte ändern und neu schreiben, dann beides an Ihren Kunden schicken.${v.zeitenFremd ? ' Die Zeiten der alten Rechnung bleiben offen. Verbinden kann sie, wer alle Zeiten sieht.' : ''}`,
          );
        } else {
          setArt(v.art);
          setNote(v.note ?? '');
          setHinweis(`Kopie von ${old ? old.number : 'einer alten Rechnung'}. Bitte Leistungen und Leistungsdatum prüfen.`);
        }
      })
      .catch((err) => live && fehler(err))
      .finally(() => live && setLaden(false));
    return () => {
      live = false;
    };
  }, [vorlageId, neuSchreiben]);

  // Aus einem Angebot (Angebote.tsx): Kunde, Positionen (Abschlag: je Steuersatz eine Position über den Anteil),
  // Bezug aufs Angebot im Hinweis, bei der Schlussrechnung die Abschläge zum Angebot angehakt
  useEffect(() => {
    if (!angebotParam || vorlageId || entwurfParam) return;
    const a = params.get('art');
    const zuArt: RtArt = a === 'abschlag' || a === 'schluss' ? a : 'normal';
    let live = true;
    api
      .get<AngebotRechnung>(angebotRechnungUrl(angebotParam, zuArt, zuArt === 'abschlag' ? Number(params.get('prozent')) : undefined))
      .then(({ angebot, form, warnung }) => {
        if (!live) return;
        const kundeId = form.kundeId && dataRef.current.kunden.some((k) => k.id === form.kundeId) ? form.kundeId : null;
        setAngebotId(form.angebotId);
        setKundeState(kundeWerteOf(form.customer, kundeId));
        setXrechnung(form.eFormat === 'xrechnung');
        // Währung des Angebots, der Kurs zum Rechnungsdatum neu
        wae.setW(waehrungAus(form.waehrung), false);
        setArt(form.art);
        setAuftrag(form.auftrag ?? '');
        setPicked(form.abschlaege);
        setRows(form.items.map((i) => rowOf(i)));
        steuerHinweis.laden(form.items, form.steuerHinweis);
        setSite(form.site ?? '');
        setPaymentDays(String(form.paymentDays));
        setNote(form.note);
        setHinweis(`Aus Angebot ${angebot.number} vom ${fmtDate(angebot.date)}. Bitte das Leistungsdatum prüfen.${warnung ? ` ${warnung}` : ''}`);
      })
      .catch((err) => live && fehler(err))
      .finally(() => live && setLaden(false));
    return () => {
      live = false;
    };
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [angebotParam]);

  // Aus einer Auftragsbestätigung oder aus Lieferscheinen (Auftraege.tsx): Kunde, Positionen (fehlende Preise leer),
  // Leistungsdatum aus Liefertermin bzw. Lieferdaten, Bezug und Bestellnummer im Hinweis
  useEffect(() => {
    if (!ausVorgang || vorlageId || entwurfParam || angebotParam) return;
    let live = true;
    api
      .get<AuftragRechnung>(auftragParam ? rechnungAusAuftragUrl(auftragParam) : rechnungAusLieferscheinenUrl(dataRef.current.firm.id, lsParam.split(',')))
      .then(({ aus, form, warnung }) => {
        if (!live) return;
        const kundeId = form.kundeId && dataRef.current.kunden.some((k) => k.id === form.kundeId) ? form.kundeId : null;
        setAuftragId(form.auftragId);
        setLieferscheinIds(form.lieferscheinIds);
        setKundeState(kundeWerteOf(form.customer, kundeId));
        setXrechnung(form.eFormat === 'xrechnung');
        // Währung der Auftragsbestätigung bzw. der Lieferscheine, der Kurs zum Rechnungsdatum neu
        wae.setW(waehrungAus(form.waehrung), false);
        setArt('normal');
        setRows(form.items.map((i) => rowOf({ ...i, price: i.price ?? undefined })));
        steuerHinweis.laden(form.items, form.steuerHinweis);
        if (form.serviceFrom) setServiceFrom(form.serviceFrom);
        setServiceTo(form.serviceTo ?? '');
        setSite(form.site ?? '');
        setPaymentDays(String(form.paymentDays));
        setNote(form.note);
        const von = auftragParam
          ? `Aus Auftrag ${aus[0]?.number ?? ''} vom ${aus[0] ? fmtDate(aus[0].date) : ''}.`
          : aus.length === 1
            ? `Aus Lieferschein ${aus[0]!.number} vom ${fmtDate(aus[0]!.date)}.`
            : `Aus ${aus.length} Lieferscheinen: ${aus.map((x) => x.number).join(', ')}.`;
        const preise = form.items.some((i) => i.price === null) ? ' Bitte die fehlenden Preise eintragen.' : '';
        setHinweis(`${von} Bitte das Leistungsdatum prüfen.${preise}${warnung ? ` ${warnung}` : ''}`);
      })
      .catch((err) => live && fehler(err))
      .finally(() => live && setLaden(false));
    return () => {
      live = false;
    };
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auftragParam, lsParam]);

  // Entwurf weiterschreiben
  useEffect(() => {
    const id = entwurfParam;
    if (!id) return;
    let live = true;
    api
      .get<{ daten: unknown; geaendert: string; stundenzettel?: KontaktDatei[] }>(`/api/rechnungstool/entwurf/${id}`)
      .then((e) => {
        if (!live) return;
        const d = ausEntwurf(e.daten, dataRef.current);
        setArt(d.art);
        setAuftrag(d.auftrag);
        setPicked(d.picked);
        setKundeState(d.kunde);
        setXrechnung(d.xrechnung);
        setRows(d.rows.length ? d.rows.map((r) => ({ ...emptyRow(), ...r, touched: true })) : [emptyRow()]);
        steuerHinweis.laden([], d.steuerHinweis);
        setServiceFrom(d.serviceFrom);
        setServiceTo(d.serviceTo);
        setPaymentDays(d.paymentDays);
        setSkonto(d.skonto);
        setNote(d.note);
        setSite(d.site);
        setAngebotId(d.angebotId ?? null);
        setAuftragId(d.auftragId ?? null);
        setLieferscheinIds(d.lieferscheinIds ?? []);
        setZeitIds(d.zeitIds ?? []);
        setErsetzt(d.ersetzt ?? null);
        setRechnungsdatum(d.rechnungsdatum ?? dataRef.current.today);
        setNummer(d.nummer ?? '');
        wae.laden(d.waehrung ?? 'EUR', d.kurs, d.kursDatum);
        setHinweis(`Entwurf vom ${fmtDate(e.geaendert)}${d.hinweis ? `. ${d.hinweis}` : ''}`);
        entwurfHinweis.current = d.hinweis;
        setZettel(e.stundenzettel ?? []);
        if (BILLTORO && d.ersetzt) zettelVon(d.ersetzt, () => live);
      })
      .catch((err) => {
        if (!live) return;
        setEntwurfId(null);
        fehler(err);
      })
      .finally(() => live && setLaden(false));
    return () => {
      live = false;
    };
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Empfehlung aus den alten Rechnungen (Einsatzort, Anschrift oder Kunde): Positionen mit dem letzten Preis vorbelegen,
  // solange der Mandant noch nichts eingetragen hat; daneben steht, woher der Preis kommt
  // (nur alte Rechnungen in der Währung dieser Rechnung, die Preise passen sonst nicht)
  const verlauf = useMemo(() => data.verlauf.filter((v) => (v.waehrung ?? 'EUR') === wae.w), [data.verlauf, wae.w]);
  const emp = useMemo(
    () => empfehlung(verlauf, { name: kunde.name, street: kunde.street, site }),
    [verlauf, kunde.name, kunde.street, site],
  );
  const empKey = emp ? `${emp.from.number}|${emp.by}` : '';
  const empRows = () =>
    emp!.from.items.map((i) =>
      rowOf(i, {
        // Stunden ändern sich jedes Mal: Menge leer lassen
        quantity: isHourly(i.unit) ? '' : deQty(i.quantity),
        tip: `Empfehlung: ${eurC(i.price, wae.w)} je ${i.unit || 'Einheit'} · ${emp!.text}`,
      }),
    );
  useEffect(() => {
    if (laden) return;
    setRows((cur) => {
      if (!cur.every(free)) return cur;
      if (!emp) return cur.some((r) => r.tip) ? [emptyRow()] : cur;
      return empRows();
    });
    // Hinweis bei 0 % mit den Zeilen: der der alten Rechnung (alte Positionen mit „13b“: der Ersatz) statt eines vorigen
    // Vorschlags; fallen die vorgeschlagenen Zeilen weg, auch ihr Hinweis. Selbst Geschriebenes bleibt.
    if (rows.every(free) && (emp || rows.some((r) => r.tip))) steuerHinweis.vorschlagen(emp?.from.items ?? [], emp?.from.steuerHinweis, true);
    // nur wenn sich die passende alte Rechnung ändert
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empKey, laden]);
  const hourly = emp?.from.items.find((i) => isHourly(i.unit));

  // Schlussrechnung: Abschläge desselben Kunden (Name und PLZ) zum Abziehen
  const plz = (z: string) => z.replace(/\s/g, '').toUpperCase();
  const abschlaege = data.abschlaege.filter(
    (x) =>
      x.kleinunternehmer === ku &&
      (!kunde.name.trim() || x.customer.trim().toLowerCase() === kunde.name.trim().toLowerCase()) &&
      (!kunde.zip.trim() || plz(x.zip) === plz(kunde.zip)),
  );
  // nur, was in der Liste auch zu sehen ist (ein vorher angehakter Abschlag eines anderen Kunden zählt nicht)
  const abzug = abschlaege.filter((x) => art === 'schluss' && picked.includes(x.id));
  const abzugGross = Math.round(abzug.reduce((s, x) => s + x.gross, 0) * 100);
  const total = summen(rows, ku).gross - abzugGross;

  // Rechnungsdatum (leer: heute), erlaubt vom 1. Januar des Vorjahres bis ein Jahr im Voraus (wie der Server)
  const datum = rechnungsdatum || data.today;
  const datumMin = `${Number(data.today.slice(0, 4)) - 1}-01-01`;
  const datumMax = plusTage(data.today, 365);
  // nur im Sonderfall ein Hinweis: Monate, die schon bei der Kanzlei sind, holt der DATEV-Export nicht noch einmal
  const frueherMonat = datum.slice(0, 7) < data.today.slice(0, 7);
  const naechste = data.naechsteNummer?.[datum.slice(0, 4)];

  // Vorschlag aus dem Stundenzettel: als Positionen anhängen (eine leere Startzeile wird ersetzt), Leistungszeitraum
  // aus den Tagen auf dem Zettel
  function takeKi(items: KiPosition[], from: string | null, to: string | null) {
    const added = items.map((i) => ({
      ...emptyRow(),
      description: i.description,
      quantity: deQty(i.quantity),
      unit: i.unit,
      price: i.price,
      ki: true,
    }));
    setRows([...rows.filter((r) => !free(r)), ...added]);
    if (from) {
      setServiceFrom(from);
      setServiceTo(to && to !== from ? to : '');
    }
  }

  function body() {
    return {
      customer: customerBody(kunde, { xrechnung }),
      kundeId: kunde.kundeId,
      entwurfId,
      angebotId,
      auftragId,
      lieferscheinIds,
      zeitIds,
      ersetzt,
      eFormat: xrechnung ? 'xrechnung' : 'zugferd',
      art,
      auftrag: art === 'normal' ? '' : auftrag,
      abschlaege: art === 'schluss' ? abzug.map((x) => x.id) : [],
      items: rows.map((r) => positionBody(r, ku)),
      // heute (oder leer): ohne Datum, der Server nimmt heute
      invoiceDate: datum === data.today ? null : datum,
      number: nummer.trim() || null,
      serviceFrom,
      serviceTo: serviceTo || null,
      site,
      paymentDays: paymentDays.trim() === '' ? NaN : Number(paymentDays),
      note,
      steuerHinweis: steuerHinweis.body,
      skonto: skontoBody(skonto),
      ...wae.body,
    };
  }

  async function showPreview() {
    setError(null);
    setBusy(true);
    try {
      const blob = await api.blob(`/api/rechnungstool/${data.firm.id}/vorschau`, body());
      setPreview(URL.createObjectURL(blob));
    } catch (err) {
      if (!steuerHinweis.amFeld(err)) fehler(err);
    } finally {
      setBusy(false);
    }
  }

  async function write() {
    setBusy(true);
    try {
      const res = await api.post<{ id: string; number: string }>(`/api/rechnungstool/${data.firm.id}/rechnungen`, body());
      await reload();
      toast(`${artName} ${res.number} geschrieben`);
      // gleich die PDF, mit „Per E-Mail“ und „Herunterladen“ (RechnungList.tsx)
      navigate('/rechnung/rechnungen', { state: { show: res.id, number: res.number, title: artName } });
    } catch (err) {
      setConfirm(false);
      if (!steuerHinweis.amFeld(err)) fehler(err);
      setBusy(false);
    }
  }

  // Als Entwurf speichern: ohne Prüfung und ohne Nummer, zurück zur Liste (dort oben unter „Entwürfe“)
  async function saveDraft() {
    setError(null);
    setBusy(true);
    const daten: EntwurfDaten = {
      v: 1,
      art,
      auftrag,
      picked,
      kunde,
      xrechnung,
      rows: rows.map(({ key: _k, ki, tip: _t, touched: _to, ...r }) => (ki ? { ...r, ki: true } : r)),
      serviceFrom,
      serviceTo,
      paymentDays,
      skonto,
      note,
      site,
      hinweis: hinweis?.startsWith('Entwurf vom') ? entwurfHinweis.current : hinweis,
      angebotId,
      auftragId,
      lieferscheinIds,
      zeitIds,
      ersetzt,
      steuerHinweis: steuerHinweis.text,
      // heute bleibt beim Weiterschreiben „heute“ (dann der Tag, an dem weitergeschrieben wird)
      rechnungsdatum: datum === data.today ? '' : datum,
      nummer,
      waehrung: wae.w,
      kurs: wae.body.kurs,
      kursDatum: wae.kursTag || null,
    };
    // Betrag nur für die Liste (unsinnig große Zahlen: ohne Betrag)
    const betrag = Number.isFinite(total) && Math.abs(total) < 1e11 ? total / 100 : 0;
    const b = { art: 'rechnung', titel: kunde.name.trim().slice(0, 200), betrag, daten };
    try {
      let saved = false;
      if (entwurfId) {
        try {
          await api.put(`/api/rechnungstool/entwurf/${entwurfId}`, b);
          saved = true;
        } catch (err) {
          // inzwischen gelöscht: als neuer Entwurf speichern
          if (!(err instanceof ApiError && err.status === 404)) throw err;
        }
      }
      if (!saved) setEntwurfId((await api.post<{ id: string }>(`/api/rechnungstool/${data.firm.id}/entwuerfe`, b)).id);
      toast('Als Entwurf gespeichert');
      navigate('/rechnung/rechnungen');
    } catch (err) {
      fehler(err);
      setBusy(false);
    }
  }

  const artName = ARTEN.find((a) => a[0] === art)![1];

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setZeitenLoesen(false);
    setKundeFehler(null);
    // Billtoro: Grenze des Tarifs schon erreicht, gleich das Fenster „Mehr mit Billtoro“ statt „Jetzt schreiben?“
    if (grenzeAnbieten(data.tarif, 'rechnungenMonat')) return;
    setConfirm(true);
  }

  // Bausteine, die beide Formen des Formulars benutzen (Kanzlei: alles offen; Billtoro: kompakt)
  // aus Auftragsbestätigung, Lieferscheinen oder Zeiten immer eine normale Rechnung (Abschläge gehen über das Angebot)
  const artWahl = auftragId || lieferscheinIds.length || zeitIds.length ? null : (
    <>
      <ChipRadios<RtArt> label="Art der Rechnung" className="rf-arten" value={art} options={ARTEN} onChange={(v) => setArt(v)} />
      {art !== 'normal' && (
        <div className="rt-grid">
          <p className="muted rt-span4 rt-hint">{ART_TEXT[art]}</p>
          <label className="field rt-span4">
            <span>Auftrag oder Projekt{art === 'schluss' ? ' (optional)' : ''}</span>
            <input value={auftrag} placeholder="z. B. Rohbau Musterstraße 5, Angebot 2026-014" onChange={(e) => setAuftrag(e.target.value)} />
            {art === 'abschlag' && <small>Auf der Rechnung steht: „Anzahlung für noch nicht (vollständig) ausgeführte Leistung“.</small>}
          </label>
        </div>
      )}
    </>
  );
  const einsatzort = (
    <div className="rt-grid">
      <label className="field rt-span4">
        <span>Einsatzort oder Objekt (optional)</span>
        <input
          value={site}
          placeholder="z. B. Bürohaus Kehrwieder 7, wenn die Leistung nicht an der Anschrift oben war"
          onChange={(e) => setSite(e.target.value)}
        />
      </label>
    </div>
  );
  const xrechnungWahl = <XRechnungWahl on={xrechnung} onToggle={setXrechnung} value={kunde} onChange={setKunde} absender={data.absender} />;
  // KI im Billtoro-Tarif nicht enthalten oder noch nicht da: im Formular nichts davon (die Rechnungsliste sagt es)
  const ki = data.ki && !fehlendeFunktion(data.tarif, 'ki');
  const leistungsTeil = (
    <>
      <h2>Leistungen</h2>
      {/* aus Angebot, Auftrag, Lieferscheinen oder Zeiten: die Leistungen stehen schon dort, keine Empfehlung aus alten Rechnungen */}
      {emp && !angebotId && !auftragId && !lieferscheinIds.length && !zeitIds.length && !rows.some((r) => r.tip) && (
        <div className="rt-tip">
          <div>
            <b>Empfehlung aus Ihren alten Rechnungen</b>
            <p>
              {emp.text}:{' '}
              {emp.from.items
                .slice(0, 3)
                .map((i) => `${i.description} ${eurC(i.price, wae.w)} je ${i.unit || 'Einheit'}`)
                .join(' · ')}
            </p>
          </div>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              setRows([...rows.filter((r) => !free(r)), ...empRows()]);
              steuerHinweis.vorschlagen(emp.from.items, emp.from.steuerHinweis, false);
            }}
          >
            Übernehmen
          </button>
        </div>
      )}
      {ki && (
        <Stundenzettel
          firmId={data.firm.id}
          test={data.kiTest}
          onTake={takeKi}
          rateHint={hourly && emp ? { price: dePrice(hourly.price), text: `${emp.text}: ${hourly.description}` } : null}
        />
      )}
      <Positionen
        rows={rows}
        onChange={setRows}
        ku={ku}
        rates={rates}
        leistungen={leistungen}
        leistungSpeichern={darf(data.tarif, 'kunden')}
        w={wae.w}
        kurs={wae.kurs}
        satz={satz}
      />

      <SteuerHinweisFeld h={steuerHinweis} auf="der Rechnung" />
      {art === 'schluss' && (
        <div className="rt-abz">
          <h2>Abschläge abziehen</h2>
          <p className="muted">
            Oben stehen alle Leistungen des Auftrags. Die Abschläge werden mit ihrer Umsatzsteuer abgezogen. Was von ihnen noch nicht
            bezahlt ist, steht als Hinweis auf der Schlussrechnung und bleibt zu zahlen.
          </p>
          {abschlaege.length === 0 ? (
            <div className="notice warn">
              {kunde.name.trim() ? `Keine offenen Abschlagsrechnungen an ${kunde.name}.` : 'Noch keine Abschlagsrechnungen.'}
            </div>
          ) : (
            <ul className="rt-abz-list">
              {abschlaege.map((x) => (
                <li key={x.id}>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={picked.includes(x.id)}
                      onChange={(e) => setPicked(e.target.checked ? [...picked, x.id] : picked.filter((id) => id !== x.id))}
                    />
                    <span>
                      <b className="num">{x.number}</b> vom {fmtDate(x.invoiceDate)}
                      {x.auftrag ? ` · ${x.auftrag}` : ''}
                      <br />
                      <small className="muted">
                        netto {eurC(x.net)} · USt {eurC(x.vat)} · brutto {eurC(x.gross)}
                      </small>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Summen rows={rows} ku={ku} abzug={abzugGross} w={wae.w} kurs={wae.kurs} />
    </>
  );
  const daten = (
    <div className="rt-grid rf-daten">
      <label className="field rf-datum">
        <span>Rechnungsdatum</span>
        <input type="date" value={rechnungsdatum} min={datumMin} max={datumMax} onChange={(e) => setRechnungsdatum(e.target.value)} />
      </label>
      <label className="field rf-nr">
        <span>Rechnungsnummer</span>
        <input
          value={nummer}
          maxLength={30}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder={naechste ? `${naechste} (automatisch)` : 'automatisch'}
          onChange={(e) => setNummer(e.target.value)}
        />
      </label>
      <label className="field">
        <span>{art === 'abschlag' ? 'Leistung voraussichtlich am' : 'Leistungsdatum'}</span>
        <input type="date" value={serviceFrom} onChange={(e) => setServiceFrom(e.target.value)} />
      </label>
      <label className="field">
        <span>bis (optional)</span>
        <input type="date" value={serviceTo} min={serviceFrom} onChange={(e) => setServiceTo(e.target.value)} />
      </label>
      {frueherMonat && (
        <p className="notice warn rt-span4 rf-frueher">
          {BILLTORO
            ? 'Das Datum liegt in einem früheren Monat. Ist dieser Monat bei Ihrem Steuerbüro schon abgegeben, sagen Sie ihm kurz Bescheid.'
            : 'Das Datum liegt in einem früheren Monat. Ist dieser Monat bei Ihrer Kanzlei schon abgegeben, sagen Sie ihr kurz Bescheid.'}
        </p>
      )}
    </div>
  );
  const waehrungFeld = <WaehrungFeld h={wae} bic={data.absender.bic} />;
  const zahlung = (
    <ZahlungBlock tage={paymentDays} onTage={setPaymentDays} skonto={skonto} onSkonto={setSkonto} grossCents={total} invoiceDate={datum} w={wae.w} />
  );
  const hinweisFeld = (
    <div className="rt-grid">
      <label className="field rt-span4">
        <span>Hinweis auf der Rechnung (optional)</span>
        <textarea value={note} rows={2} placeholder="z. B. Vielen Dank für Ihren Auftrag." onChange={(e) => setNote(e.target.value)} />
      </label>
    </div>
  );
  const fehlerAnzeige =
    error &&
    (kuGrenze ? (
      <div className="notice bad rt-ku">
        <span>{error}</span>
        <Link to="/rechnung/angaben#kleinunternehmer" className="btn small">
          Meine Angaben
        </Link>
      </div>
    ) : zeitenLoesen && zeitIds.length ? (
      // die Rechnung ohne die Zeiten schreiben: die Positionen bleiben, die Zeiten bleiben offen
      <div className="notice bad rt-ku">
        <span>{error}</span>
        <button
          type="button"
          className="btn small"
          onClick={() => {
            setZeitIds([]);
            setZeitenLoesen(false);
            setError(null);
            toast('Zeiten gelöst. Die Positionen bleiben.');
          }}
        >
          Zeiten lösen
        </button>
      </div>
    ) : (
      <div className="notice bad">{error}</div>
    ));
  const entwurfKnopf = (
    <button type="button" className="btn rf-entwurf" disabled={busy || laden} onClick={() => void saveDraft()}>
      Als Entwurf speichern
    </button>
  );
  const vorschauKnopf = (
    <button type="button" className="btn" disabled={busy || laden} onClick={() => void showPreview()}>
      Vorschau
    </button>
  );
  const schreibenKnopf = (
    <button type="submit" className="btn primary" disabled={busy || laden}>
      Rechnung schreiben
    </button>
  );
  const fenster = (
    <>
      <PdfDialog url={preview} title="Vorschau" onClose={() => setPreview(null)} />
      <StundenzettelFenster
        open={zettelOffen}
        titel={entwurfId ? 'Stundenzettel zum Entwurf' : 'Stundenzettel'}
        dateien={zettel}
        onClose={() => setZettelOffen(false)}
      />
      <ConfirmDialog
        open={confirm}
        title={`${artName} jetzt schreiben?`}
        text={`Sie bekommt ${nummer.trim() ? `die Nummer ${nummer.trim()}` : 'die nächste Rechnungsnummer'} und ${datum === data.today ? 'das Datum von heute' : `das Datum ${fmtDate(datum)}`}. Danach lässt sie sich nicht mehr ändern, nur stornieren. ${abzug.length ? 'Zu zahlen' : 'Betrag'}: ${eurC(total / 100, wae.w)}.`}
        confirmLabel="Ja, schreiben"
        busy={busy}
        onConfirm={() => void write()}
        onCancel={() => setConfirm(false)}
      />
    </>
  );

  // „Entwurf vom …“ bzw. woher das Formular kommt; dahinter die Stundenzettel des Entwurfs
  const hinweisZeile = (hinweis || zettel.length > 0) && (
    <p className="rt-vorlage">
      {hinweis}
      {hinweis && zettel.length > 0 && ' '}
      {zettel.length > 0 && (
        <button type="button" className="linkbtn rf-zettel" onClick={() => setZettelOffen(true)}>
          Stundenzettel ({zettel.length})
        </button>
      )}
    </p>
  );

  const kundeFehlerZeile = kundeFehler && (
    <p className="notice bad" role="alert" ref={kundeFehlerRef}>
      {kundeFehler}
    </p>
  );

  if (BILLTORO) {
    return (
      <Kompakt
        data={data}
        kundeFehler={kundeFehlerZeile}
        art={art}
        artName={artName}
        hinweis={hinweisZeile}
        artWahl={artWahl}
        kunde={kunde}
        setKunde={setKunde}
        onPick={onPick}
        xrechnung={xrechnung}
        xrechnungWahl={xrechnungWahl}
        einsatzort={einsatzort}
        leistungen={leistungsTeil}
        weitere={{ datum, serviceFrom, serviceTo, nummer, paymentDays, skonto, note, site, xrechnung, waehrung: wae.w }}
        felder={
          <>
            {daten}
            {waehrungFeld}
            {zahlung}
            {hinweisFeld}
          </>
        }
        offenBeiFehler={!!error || wae.fremd}
        fehlerAnzeige={fehlerAnzeige}
        betrag={total}
        w={wae.w}
        zuZahlen={abzug.length > 0}
        knoepfe={{ entwurf: entwurfKnopf, vorschau: vorschauKnopf, schreiben: schreibenKnopf }}
        onSubmit={submit}
        fenster={fenster}
      />
    );
  }

  return (
    <form className="rt-card rf" onSubmit={submit} noValidate>
      <div className="rt-form-head">
        <h1>Neue {artName}</h1>
        <Link to="/rechnung/rechnungen" className="btn small">
          Abbrechen
        </Link>
      </div>
      {hinweisZeile}

      {artWahl}

      <h2>Kunde</h2>
      <KundeWahl kunden={data.kunden} items={data.items} today={data.today} value={kunde} onChange={setKunde} onPick={onPick} />
      {kundeFehlerZeile}
      {einsatzort}
      {xrechnungWahl}

      {leistungsTeil}

      {daten}
      {waehrungFeld}
      {zahlung}
      {hinweisFeld}
      {fehlerAnzeige}
      <div className="rt-actions rf-actions">
        {entwurfKnopf}
        {vorschauKnopf}
        {schreibenKnopf}
      </div>

      {fenster}
    </form>
  );
}

// Billtoro: das Formular kompakt, damit die erste Rechnung am Handy so einfach geht wie eine Nachricht (Sam: „Rechnungen
// schreiben so einfach wie eine Nachricht“). Sichtbar sind nur Kunde (Kundenart Firma, „andere Kundenart“ klappt die
// Wahl auf), Leistungen und Betrag. Art der Rechnung hinter „Abschlags- oder Schlussrechnung?“, Daten, Nummer,
// Zahlungsziel, Skonto, Hinweis, Zusatz, Einsatzort und XRechnung (außer bei Behörden) hinter „Weitere Angaben“ mit
// einer Zeile, was gerade gilt („Heute · Nr. automatisch · zahlbar in 14 Tagen“). Am Handy bleibt unten eine Leiste
// mit dem Betrag und „Rechnung schreiben“ stehen.
function Kompakt(p: {
  data: Overview;
  kundeFehler: ReactNode;
  art: RtArt;
  artName: string;
  hinweis: ReactNode;
  artWahl: ReactNode;
  kunde: KundeWerte;
  setKunde(v: KundeWerte): void;
  onPick(k: Kunde | null): void;
  xrechnung: boolean;
  xrechnungWahl: ReactNode;
  einsatzort: ReactNode;
  leistungen: ReactNode;
  weitere: {
    datum: string;
    serviceFrom: string;
    serviceTo: string;
    nummer: string;
    paymentDays: string;
    skonto: SkontoWerte | null;
    note: string;
    site: string;
    xrechnung: boolean;
    waehrung: Waehrung;
  };
  felder: ReactNode;
  offenBeiFehler: boolean;
  fehlerAnzeige: ReactNode;
  betrag: number;
  w: Waehrung;
  zuZahlen: boolean;
  knoepfe: { entwurf: ReactNode; vorschau: ReactNode; schreiben: ReactNode };
  onSubmit(e: FormEvent): void;
  fenster: ReactNode;
}) {
  const { data, art, kunde } = p;
  const schmal = useSchmal();
  const [artOffen, setArtOffen] = useState(false);
  const [kundenartOffen, setKundenartOffen] = useState(false);
  const [mehr, setMehr] = useState(false);
  // eine Meldung vom Server betrifft oft ein Feld unter „Weitere Angaben“: dann aufklappen; ebenso bei Fremdwährung
  // (der Kurs steht dort)
  useEffect(() => {
    if (p.offenBeiFehler) setMehr(true);
  }, [p.offenBeiFehler]);
  const artSichtbar = artOffen || art !== 'normal';
  const kundenartSichtbar = kundenartOffen || kunde.art !== 'firma';
  // XRechnung gehört bei Behörden nach oben (fast immer nötig), sonst zu den weiteren Angaben
  const behoerde = kunde.art === 'behoerde';
  const zeile = weitereZeile(p.weitere, data.today, kunde);

  return (
    <>
      <NutzungHinweis tarif={data.tarif} wo="formular" />
      <form className="rt-card rf rf-kompakt" onSubmit={p.onSubmit} noValidate>
        <div className="rt-form-head">
          <h1>Neue {p.artName}</h1>
          <Link to="/rechnung/rechnungen" className="btn small">
            Abbrechen
          </Link>
        </div>
        {p.hinweis}

        {/* ohne Wahl (Rechnung aus Auftrag oder Lieferscheinen) auch ohne den Link */}
        {p.artWahl &&
          (artSichtbar ? (
            p.artWahl
          ) : (
            <button type="button" className="linkbtn rf-klein rf-art-link" onClick={() => setArtOffen(true)}>
              Abschlags- oder Schlussrechnung?
            </button>
          ))}

        <div className="rf-h2">
          <h2>Kunde</h2>
          {!kundenartSichtbar && (
            <button type="button" className="linkbtn rf-klein" onClick={() => setKundenartOffen(true)}>
              andere Kundenart
            </button>
          )}
        </div>
        <KundeWahl
          kunden={data.kunden}
          items={data.items}
          today={data.today}
          value={kunde}
          onChange={p.setKunde}
          onPick={p.onPick}
          ohneArt={!kundenartSichtbar}
          ohneZusatz
        />
        {p.kundeFehler}
        {behoerde && p.xrechnungWahl}

        {p.leistungen}

        <button type="button" className="rf-mehr" aria-expanded={mehr} aria-controls="rf-mehr" onClick={() => setMehr(!mehr)}>
          <b>Weitere Angaben</b>
          <span>
            {mehr ? (
              <u>Zuklappen</u>
            ) : (
              <>
                {zeile.join(' · ')} · <u>Ändern</u>
              </>
            )}
          </span>
        </button>
        {mehr && (
          <div id="rf-mehr" className="rf-mehr-b">
            {p.felder}
            <div className="rt-grid">
              <label className="field rt-span4">
                <span>Zusatz zur Anschrift (optional)</span>
                <input value={kunde.extra} placeholder="z. B. z. Hd. Frau Meier" onChange={(e) => p.setKunde({ ...kunde, extra: e.target.value })} />
              </label>
            </div>
            {p.einsatzort}
            {!behoerde && p.xrechnungWahl}
          </div>
        )}
        {p.fehlerAnzeige}

        {schmal ? (
          <>
            <div className="rf-leiste">
              <span className="rf-leiste-b">
                <small>{p.zuZahlen ? 'Zu zahlen' : 'Rechnungsbetrag'}</small>
                <b className="num">{eurC(p.betrag / 100, p.w)}</b>
              </span>
              {p.knoepfe.schreiben}
            </div>
            <div className="rt-actions rf-actions rf-neben">
              {p.knoepfe.entwurf}
              {p.knoepfe.vorschau}
            </div>
          </>
        ) : (
          <div className="rt-actions rf-actions">
            {p.knoepfe.entwurf}
            {p.knoepfe.vorschau}
            {p.knoepfe.schreiben}
          </div>
        )}

        {p.fenster}
      </form>
    </>
  );
}

// Was unter „Weitere Angaben“ gerade gilt, in einer Zeile: „Heute · Nr. automatisch · zahlbar in 14 Tagen“, dazu
// nur, was davon abweicht oder eingetragen ist
function weitereZeile(w: Parameters<typeof Kompakt>[0]['weitere'], today: string, kunde: KundeWerte): string[] {
  const teile = [w.datum === today ? 'Heute' : fmtDate(w.datum)];
  if (w.serviceTo) teile.push(`Leistung ${fmtDate(w.serviceFrom)} bis ${fmtDate(w.serviceTo)}`);
  else if (w.serviceFrom && w.serviceFrom !== w.datum) teile.push(`Leistung am ${fmtDate(w.serviceFrom)}`);
  teile.push(w.nummer.trim() ? `Nr. ${w.nummer.trim()}` : 'Nr. automatisch');
  if (w.waehrung !== 'EUR') teile.push(w.waehrung);
  const tage = /^\d+$/.test(w.paymentDays.trim()) ? Number(w.paymentDays) : null;
  teile.push(tage === null ? 'Zahlungsziel fehlt' : tage === 0 ? 'zahlbar sofort' : `zahlbar in ${tage} ${tage === 1 ? 'Tag' : 'Tagen'}`);
  if (w.skonto) teile.push(`${w.skonto.prozent} % Skonto`);
  if (kunde.extra.trim()) teile.push(kunde.extra.trim());
  if (w.site.trim()) teile.push(w.site.trim());
  if (w.xrechnung && kunde.art !== 'behoerde') teile.push('XRechnung');
  if (w.note.trim()) teile.push('mit Hinweis');
  return teile;
}

const artOfKunde = (k: Kunde): Art => kundeWerteOf(k).art;

// Formularstand aus einem Entwurf lesen, vorsichtig (gespeichert ist, was der Browser geschickt hat): Unbekanntes oder
// Kaputtes fällt auf die Vorgaben zurück. Ein Kunde, der inzwischen aus der Liste entfernt wurde, gilt als neu.
function ausEntwurf(raw: unknown, data: Overview): Omit<EntwurfDaten, 'v'> {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const str = (v: unknown, d = '') => (typeof v === 'string' ? v : d);
  const k = (o.kunde && typeof o.kunde === 'object' ? o.kunde : {}) as Record<string, unknown>;
  const arten: Art[] = ['firma', 'privat', 'behoerde', 'eu', 'welt'];
  const kundeId = typeof k.kundeId === 'string' && data.kunden.some((x) => x.id === k.kundeId) ? k.kundeId : null;
  const kunde: KundeWerte = {
    kundeId,
    art: arten.includes(k.art as Art) ? (k.art as Art) : 'firma',
    name: str(k.name),
    extra: str(k.extra),
    street: str(k.street),
    zip: str(k.zip),
    city: str(k.city),
    country: str(k.country, 'AT'),
    land: str(k.land, 'CH'),
    weltPrivat: k.weltPrivat === true,
    vatId: str(k.vatId),
    email: str(k.email),
    buyerRef: str(k.buyerRef),
  };
  // ältere Entwürfe: Positionen mit „13b“ oder „eu“ werden 0 % (formular.ts rateAus, Hinweis aus hinweisAus); der
  // frühere Haken „Leistung an einem Grundstück“ zählt nicht mehr (der Server entscheidet nach der Branche)
  const zeilen = (Array.isArray(o.rows) ? o.rows : []).slice(0, 50).map((x) => (x && typeof x === 'object' ? x : {}) as Record<string, unknown>);
  const rows = zeilen.map((r) => ({
    description: str(r.description),
    quantity: str(r.quantity),
    unit: str(r.unit),
    price: str(r.price),
    rate: rateAus(r.rate) ?? 19,
    discount: str(r.discount),
    ...(r.ki === true ? { ki: true } : {}),
  }));
  const s = o.skonto && typeof o.skonto === 'object' ? (o.skonto as Record<string, unknown>) : null;
  const art: RtArt = o.art === 'abschlag' || o.art === 'schluss' ? o.art : 'normal';
  return {
    art,
    auftrag: str(o.auftrag),
    picked: (Array.isArray(o.picked) ? o.picked : []).filter((x): x is string => typeof x === 'string'),
    kunde,
    xrechnung: o.xrechnung === true,
    rows,
    serviceFrom: str(o.serviceFrom, data.today) || data.today,
    serviceTo: str(o.serviceTo),
    paymentDays: str(o.paymentDays, String(data.absender.paymentDays)),
    skonto: s ? { prozent: str(s.prozent), tage: str(s.tage) } : null,
    note: str(o.note),
    site: str(o.site),
    hinweis: typeof o.hinweis === 'string' ? o.hinweis : null,
    angebotId: typeof o.angebotId === 'string' ? o.angebotId : null,
    auftragId: typeof o.auftragId === 'string' ? o.auftragId : null,
    lieferscheinIds: (Array.isArray(o.lieferscheinIds) ? o.lieferscheinIds : []).filter((x): x is string => typeof x === 'string').slice(0, 20),
    zeitIds: (Array.isArray(o.zeitIds) ? o.zeitIds : []).filter((x): x is string => typeof x === 'string').slice(0, 500),
    ersetzt: typeof o.ersetzt === 'string' ? o.ersetzt : null,
    steuerHinweis: hinweisAus(zeilen, o.steuerHinweis, data.hinweis0, drittlandArt(kunde)),
    rechnungsdatum: /^\d{4}-\d{2}-\d{2}$/.test(str(o.rechnungsdatum)) ? str(o.rechnungsdatum) : data.today,
    nummer: str(o.nummer).slice(0, 30),
    waehrung: waehrungAus(o.waehrung),
    kurs: typeof o.kurs === 'string' ? o.kurs : null,
    kursDatum: typeof o.kursDatum === 'string' ? o.kursDatum : null,
  };
}
